/**
 * MONSTER TOWN — WHAT A NEIGHBOUR DECIDES TO DO. NPC CREW (src/npc/**).
 *
 * Not a behaviour tree, not a planner: a weighted pick from a short list, run
 * only when a neighbour is standing still and has nothing else on. That is all
 * a cosy town needs, and it costs nothing on an old iPad.
 *
 * THE POINT OF ALL OF IT is that the town is worth watching:
 *   - they walk over to things the kid built and like them (`npc:admired`)
 *   - they come and say hello when the kid is nearby
 *   - they nap, dance, sit and potter about where the kid can see it
 * No neighbour ever demands anything, blocks anything or can be got wrong.
 */

import { bus } from '../core/events'
import type { Grid, Placement } from '../core/grid'
import type { SceneNode } from '../core/scene'
import type { InstanceId, Vec3 } from '../core/types'
import { standoffPoint } from '../monster/walker'
import type { Npc } from './npc'

/** Sentinel for `npc:reacted.to` when the neighbour reacted to the kid. */
export const PLAYER_TARGET = 'player' as InstanceId

export interface BrainContext {
  readonly node: SceneNode
  readonly grid: Grid
  readonly playerPos: Vec3
  readonly rng: () => number
  /** Where the kid's front door is, if the world crew has built one yet. */
  readonly homeSpot: Vec3 | null
}

const GREET_RANGE = 3.4
const GREET_COOLDOWN = 16
const ADMIRE_RANGE = 22

/** A random walkable world point near `from`, or `from` if the area is packed. */
export function roamPoint(grid: Grid, from: Vec3, rng: () => number, radiusTiles = 7): Vec3 {
  const c = grid.worldToCell(from.x, from.z, 0)
  for (let i = 0; i < 14; i++) {
    const gx = c.gx + Math.round((rng() * 2 - 1) * radiusTiles)
    const gz = c.gz + Math.round((rng() * 2 - 1) * radiusTiles)
    if (gx < 0 || gz < 0 || gx >= grid.spec.width || gz >= grid.spec.depth) continue
    if (!grid.isWalkable(gx, gz)) continue
    return grid.cellToWorld({ gx, gy: 0, gz })
  }
  return from
}

/** Something the kid built that is worth walking over to look at. */
function admireTarget(grid: Grid, from: Vec3, rng: () => number): Placement | null {
  const all = grid.all().filter((p) => !p.walkable && p.anchor.gy === 0)
  if (all.length === 0) return null
  const near: Placement[] = []
  for (const p of all) {
    const c = grid.footprintCenter(p.anchor, p.footprint, p.rot)
    if (Math.hypot(c.x - from.x, c.z - from.z) <= ADMIRE_RANGE) near.push(p)
  }
  const pool = near.length > 0 ? near : all
  return pool[Math.floor(rng() * pool.length)] ?? null
}

const ADMIRE_ACTIONS = ['nod', 'clap', 'point', 'think', 'cheer'] as const
const POTTER_ACTIONS = ['lookAround', 'wiggle', 'shrug', 'hop', 'laugh'] as const
const WORK_ACTIONS = ['carry', 'nod', 'point', 'clap', 'think'] as const
const WAKE_ACTIONS = ['hop', 'wiggle', 'lookAround', 'shrug'] as const

/**
 * WHAT THE HOUR SUGGESTS. "A DAY IN TOWN" §2.
 *
 * A neighbour's schedule does not command them — it re-weights the same short
 * list of pleasant things they were already choosing from. At breakfast they
 * mostly eat; out and about they mostly wander; behind a counter they mostly
 * potter at their post. Every table keeps a little of everything else, so a
 * neighbour is never a clockwork toy, and NOTHING here can be got wrong: an
 * activity that cannot resolve just falls through to a wander.
 *
 * `stay` = do something where you are. `roam` = a short walk nearby. The
 * DIRECTOR (src/routine/**) owns the long walk across town; the brain only ever
 * fills the time in between.
 */
function routineWeights(activity: string): Record<string, number> {
  switch (activity) {
    case 'asleep':
      return { snooze: 100 }
    case 'waking':
      return { stretch: 70, potter: 20, roam: 10 }
    case 'breakfast':
    case 'dinner':
      return { eat: 60, stay: 20, potter: 15, roam: 5 }
    case 'shopping':
      return { stay: 35, potter: 30, admire: 20, roam: 15 }
    case 'visiting':
      return { visit: 45, admire: 25, potter: 20, roam: 10 }
    case 'working':
      return { work: 55, stay: 20, potter: 15, roam: 10 }
    case 'bath':
      return { stay: 45, potter: 35, dance: 20 }
    case 'winddown':
      return { sit: 45, stay: 20, potter: 20, admire: 15 }
    case 'bed':
      return { roam: 40, potter: 35, stay: 25 }
    case 'out':
    default:
      return { wander: 40, admire: 24, dance: 8, sit: 8, potter: 20 }
  }
}

function weightedActivity(likes: string, rng: () => number): string {
  // Base weights, then the archetype's favourite gets a big thumb on the scale.
  const table: Record<string, number> = {
    wander: 34,
    admire: 20,
    visit: 10,
    dance: 8,
    nap: 8,
    sit: 8,
    potter: 12,
  }
  if (table[likes] !== undefined) table[likes] = (table[likes] ?? 0) + 45
  return roll(table, rng)
}

function roll(table: Record<string, number>, rng: () => number): string {
  let total = 0
  for (const v of Object.values(table)) total += v
  let r = rng() * total
  for (const [k, v] of Object.entries(table)) {
    r -= v
    if (r <= 0) return k
  }
  return 'wander'
}

/**
 * Called every fixed step for every neighbour. Cheap: the interesting branch
 * only runs when they are standing still with an expired timer.
 */
export function think(npc: Npc, c: BrainContext): void {
  if (npc.scripted) return
  // Gone in for the night. An invisible monster waving at a passing kid is a
  // bug, and one asleep in their own bed does not need a decision made for them.
  if (npc.indoors) return

  // 1. Say hello. This is the single most important NPC behaviour in the game:
  //    the kid walks somewhere and a monster notices them.
  if (npc.greetCooldown <= 0 && !npc.walking) {
    const d = Math.hypot(c.playerPos.x - npc.walker.x, c.playerPos.z - npc.walker.z)
    if (d <= GREET_RANGE) {
      npc.faceTowards(c.playerPos)
      npc.act('wave')
      npc.mood = 'delighted'
      npc.activity = 'greet'
      npc.timer = 2
      npc.greetCooldown = GREET_COOLDOWN
      // `to` is an InstanceId in the core event; the sentinel 'player' means the
      // neighbour reacted to the kid rather than to a placed thing.
      bus.emit('npc:reacted', { npcId: npc.id, to: PLAYER_TARGET, mood: 'delighted' })
      bus.emit('audio:cue', { cue: 'npc_hello', at: npc.position, volume: 0.5 })
      return
    }
  }

  // Held poses (dance, nap, sit) run until their timer says stop. Ease out and
  // let the next tick pick something new.
  if (npc.timer <= 0 && npc.busyActing && !npc.walking) {
    npc.stopActing()
    return
  }

  if (npc.timer > 0 || npc.walking || npc.busyActing) return

  const choice =
    npc.routine === null
      ? weightedActivity(npc.archetype.likes, c.rng)
      : roll(routineWeights(npc.routine), c.rng)

  switch (choice) {
    // -- the schedule's own beats ----------------------------------------
    case 'snooze': {
      npc.activity = 'nap'
      npc.mood = 'sleepy'
      npc.act('sleep', 0)
      npc.timer = 9 + c.rng() * 6
      return
    }
    case 'stretch': {
      npc.activity = 'idle'
      npc.mood = 'happy'
      npc.act(WAKE_ACTIONS[Math.floor(c.rng() * WAKE_ACTIONS.length)] ?? 'wiggle')
      npc.timer = 1.6 + c.rng() * 2
      return
    }
    case 'eat': {
      npc.activity = 'idle'
      npc.mood = 'happy'
      npc.act('eat', 2.2)
      npc.timer = 3 + c.rng() * 3
      return
    }
    case 'work': {
      npc.activity = 'idle'
      npc.mood = 'curious'
      npc.act(WORK_ACTIONS[Math.floor(c.rng() * WORK_ACTIONS.length)] ?? 'carry')
      npc.timer = 2.5 + c.rng() * 3
      return
    }
    case 'stay': {
      npc.activity = 'idle'
      npc.timer = 2 + c.rng() * 4
      return
    }
    case 'roam': {
      // A short shuffle, not a march across town — the director owns the long
      // walk, and two systems steering one monster looks like indecision.
      npc.activity = 'wander'
      npc.walkTo(roamPoint(c.grid, npc.position, c.rng, 3), 'lookAround')
      npc.timer = 3 + c.rng() * 3
      return
    }

    // -- the original town-life beats, unchanged --------------------------
    case 'admire': {
      const p = admireTarget(c.grid, npc.position, c.rng)
      if (p === null) break
      const centre = c.grid.footprintCenter(p.anchor, p.footprint, p.rot)
      const spot = standoffPoint(npc.position, centre, 1.5)
      const action = ADMIRE_ACTIONS[Math.floor(c.rng() * ADMIRE_ACTIONS.length)] ?? 'nod'
      npc.activity = 'admire'
      npc.focus = p.instanceId
      npc.walkTo(spot, action)
      npc.timer = 6 + c.rng() * 4
      bus.emit('npc:admired', { npcId: npc.id, instanceId: p.instanceId, sceneId: npc.sceneId })
      return
    }
    case 'visit': {
      const spot = c.homeSpot ?? c.playerPos
      npc.activity = 'visit'
      npc.walkTo(standoffPoint(npc.position, spot, 1.8), 'knock')
      npc.timer = 8 + c.rng() * 4
      bus.emit('npc:visited', { npcId: npc.id, sceneId: npc.sceneId })
      return
    }
    case 'dance': {
      npc.activity = 'dance'
      npc.act('dance', 0)
      npc.timer = 5 + c.rng() * 5
      return
    }
    case 'nap': {
      npc.activity = 'nap'
      npc.mood = 'sleepy'
      npc.act('sleep', 0)
      npc.timer = 7 + c.rng() * 8
      return
    }
    case 'sit': {
      npc.activity = 'sit'
      npc.act('sit', 0)
      npc.timer = 5 + c.rng() * 6
      return
    }
    case 'potter': {
      npc.activity = 'idle'
      npc.act(POTTER_ACTIONS[Math.floor(c.rng() * POTTER_ACTIONS.length)] ?? 'lookAround')
      npc.timer = 2 + c.rng() * 3
      return
    }
    default:
      break
  }

  npc.activity = 'wander'
  npc.mood = 'happy'
  npc.walkTo(roamPoint(c.grid, npc.position, c.rng), 'lookAround')
  npc.timer = 3 + c.rng() * 4
}
